import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { Club, User, GroupRide } from '../../types';
import ClubCreateModal from './ClubCreateModal';
import ClubDetailView from './ClubDetailView';
import './ClubsListView.css';

interface ClubsListViewProps {
  user: User;
  onClubsChanged: () => void;
  onNavigateToClub?: (clubId: number) => void;
  onCreateGroupRide?: () => void;
  onSelectRide?: (rideId: number) => void;
}

interface ClubInvitation {
  club_id: number;
  club_name: string;
  club_short_name: string;
  invited_by: string | null;
  invited_at: string;
}

const ClubsListView: React.FC<ClubsListViewProps> = ({ user, onClubsChanged, onNavigateToClub, onCreateGroupRide, onSelectRide }) => {
  const { t } = useLanguage();
  const [myClubs, setMyClubs] = useState<Club[]>([]);
  const [invitations, setInvitations] = useState<ClubInvitation[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedClubId, setSelectedClubId] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [invitationLoading, setInvitationLoading] = useState<number | null>(null);
  const [upcomingRides, setUpcomingRides] = useState<GroupRide[]>([]);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchMyClubs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setMyClubs(data.clubs || []);
    } catch {
      setError(t('clubs.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchInvitations = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/invitations`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setInvitations(data.invitations || []);
    } catch { /* ignore */ }
  }, []);

  const fetchUpcomingRides = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/group-rides?scope=upcoming&limit=5`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setUpcomingRides(data.rides || []);
    } catch { /* ignore */ }
  }, []);

  const handleAcceptInvitation = async (clubId: number) => {
    setInvitationLoading(clubId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/invitation/accept`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        await fetchInvitations();
        await fetchMyClubs();
        onClubsChanged();
      }
    } catch { /* ignore */ }
    setInvitationLoading(null);
  };

  const handleDeclineInvitation = async (clubId: number) => {
    setInvitationLoading(clubId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/${clubId}/invitation/decline`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        await fetchInvitations();
      }
    } catch { /* ignore */ }
    setInvitationLoading(null);
  };

  useEffect(() => {
    fetchMyClubs();
    fetchInvitations();
    fetchUpcomingRides();
  }, [fetchMyClubs, fetchInvitations, fetchUpcomingRides]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/clubs/search?q=${encodeURIComponent(searchQuery)}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error();
        const data = await res.json();
        setSearchResults(data.clubs || []);
      } catch { /* ignore */ }
      setSearchLoading(false);
    }, 400);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  const handleJoin = async (club: Club) => {
    setActionLoading(club.id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/clubs/${club.id}/join`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        await fetchMyClubs();
        onClubsChanged();
        // Refresh search results to reflect new state
        if (searchQuery.length >= 2) {
          const res2 = await fetch(
            `${API_BASE_URL}/api/clubs/search?q=${encodeURIComponent(searchQuery)}`,
            { credentials: 'include' }
          );
          if (res2.ok) {
            const data = await res2.json();
            setSearchResults(data.clubs || []);
          }
        }
      }
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const handleCancelRequest = async (club: Club) => {
    setActionLoading(club.id);
    try {
      if (!club.pending_request_id) return;
      const requestId = club.pending_request_id;
      const res = await fetch(`${API_BASE_URL}/api/clubs/requests/${requestId}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        await fetchMyClubs();
        onClubsChanged();
        if (searchQuery.length >= 2) {
          const res2 = await fetch(
            `${API_BASE_URL}/api/clubs/search?q=${encodeURIComponent(searchQuery)}`,
            { credentials: 'include' }
          );
          if (res2.ok) {
            const data = await res2.json();
            setSearchResults(data.clubs || []);
          }
        }
      }
    } catch { /* ignore */ }
    setActionLoading(null);
  };

  const handleCreated = async (club: Club) => {
    setShowCreateModal(false);
    await fetchMyClubs();
    onClubsChanged();
  };

  const handleCardClick = (club: Club) => {
    setSelectedClubId(club.id);
  };

  if (selectedClubId !== null) {
    return (
      <ClubDetailView
        clubId={selectedClubId}
        user={user}
        onBack={() => {
          setSelectedClubId(null);
          fetchMyClubs();
        }}
        onClubsChanged={() => {
          fetchMyClubs();
          onClubsChanged();
        }}
      />
    );
  }

  return (
    <div className="clubs-list-view">
      {/* Header */}
      <div className="clubs-list-header">
        <h1 className="clubs-list-title">{t('clubs.title')}</h1>
        {onCreateGroupRide && (
          <button
            className="clubs-create-btn"
            onClick={onCreateGroupRide}
          >
            + {t('createGroupRide.title')}
          </button>
        )}
        <button
          className="clubs-create-btn"
          onClick={() => setShowCreateModal(true)}
        >
          + {t('clubs.createClub')}
        </button>
      </div>

      {/* Search */}
      <div className="clubs-search-section">
        <input
          type="text"
          className="clubs-search-input"
          placeholder={t('clubs.searchPlaceholder')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {searchLoading && <span className="clubs-search-loading">...</span>}
      </div>

      {/* Search Results */}
      {searchQuery.length >= 2 && (
        <div className="clubs-search-results">
          <h3 className="clubs-section-title">
            {searchResults.length > 0
              ? `${searchResults.length} clubs`
              : t('clubs.noResults')}
          </h3>
          {searchResults.map(club => (
            <div key={club.id} className="clubs-search-card">
              <div
                className="clubs-search-card-info"
                style={{ cursor: club.is_member ? 'pointer' : 'default' }}
                onClick={() => club.is_member && setSelectedClubId(club.id)}
              >
                <span className="clubs-search-card-name">{club.name}</span>
                <span className="clubs-search-card-short">{club.short_name}</span>
                <span className="clubs-search-card-count">
                  {t('clubs.memberCount').replace('{{count}}', String(club.member_count))}
                </span>
              </div>
              <div className="clubs-search-card-action">
                {club.is_member ? (
                  <span className="clubs-badge clubs-badge-joined">{t('clubs.joined')}</span>
                ) : club.has_pending_request ? (
                  <>
                    <span className="clubs-badge clubs-badge-pending">{t('clubs.pending')}</span>
                    <button
                      className="clubs-action-btn clubs-action-btn-cancel"
                      onClick={() => handleCancelRequest(club)}
                      disabled={actionLoading === club.id}
                    >
                      {t('clubs.cancelRequest')}
                    </button>
                  </>
                ) : (
                  <button
                    className="clubs-action-btn"
                    onClick={() => handleJoin(club)}
                    disabled={actionLoading === club.id}
                  >
                    {t('clubs.requestJoin')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <div className="clubs-invitations-section">
          <h2 className="clubs-section-title">{t('clubs.pendingInvitations')}</h2>
          {invitations.map(inv => (
            <div key={inv.club_id} className="clubs-invitation-card">
              <div className="clubs-invitation-info">
                <span className="clubs-invitation-name">{inv.club_name}</span>
                {inv.club_short_name && (
                  <span className="clubs-invitation-short">{inv.club_short_name}</span>
                )}
                {inv.invited_by && (
                  <span className="clubs-invitation-by">
                    {t('clubs.invitedBy')}: {inv.invited_by}
                  </span>
                )}
              </div>
              <div className="clubs-invitation-actions">
                <button
                  className="clubs-action-btn"
                  onClick={() => handleAcceptInvitation(inv.club_id)}
                  disabled={invitationLoading === inv.club_id}
                >
                  {t('clubs.acceptInvite')}
                </button>
                <button
                  className="clubs-action-btn clubs-action-btn-cancel"
                  onClick={() => handleDeclineInvitation(inv.club_id)}
                  disabled={invitationLoading === inv.club_id}
                >
                  {t('clubs.declineInvite')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upcoming Rides */}
      {upcomingRides.length > 0 && (
        <div className="clubs-rides-section">
          <h2 className="clubs-section-title">{t('createGroupRide.title') || 'Upcoming Rides'}</h2>
          {upcomingRides.map(ride => (
            <div
              key={ride.id}
              className="clubs-ride-card"
              onClick={() => onSelectRide?.(ride.id)}
            >
              <div className="clubs-ride-card-title">{ride.title}</div>
              <div className="clubs-ride-card-meta">
                <span className="clubs-ride-card-date">
                  {new Date(ride.ride_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                </span>
                {ride.start_location_name && (
                  <span className="clubs-ride-card-location">{ride.start_location_name}</span>
                )}
                <span className="clubs-ride-card-participants">{ride.participant_count} participants</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* My Clubs */}
      <div className="clubs-my-section">
        {!searchQuery && <h2 className="clubs-section-title">{t('clubs.title')}</h2>}

        {loading ? (
          <div className="clubs-loading">
            {[1, 2].map(i => (
              <div key={i} className="clubs-skeleton-card" />
            ))}
          </div>
        ) : error ? (
          <p className="clubs-error">{error}</p>
        ) : myClubs.length === 0 ? (
          <div className="clubs-empty">
            <p>{t('clubs.noClubs')}</p>
            <p className="clubs-empty-subtitle">{t('clubs.noClubsSubtitle')}</p>
          </div>
        ) : (
          <div className="clubs-card-grid">
            {myClubs.map(club => (
              <div
                key={club.id}
                className="clubs-card"
                onClick={() => handleCardClick(club)}
              >
                <div className="clubs-card-top">
                  <div className="clubs-card-name">{club.name}</div>
                  {club.short_name && (
                    <span className="clubs-card-short">{club.short_name}</span>
                  )}
                </div>
                <div className="clubs-card-bottom">
                  <span className="clubs-card-count">
                    {t('clubs.memberCount').replace('{{count}}', String(club.member_count))}
                  </span>
                  <div className="clubs-card-badges">
                    {club.role && (
                      <span className={`clubs-role-badge clubs-role-${club.role}`}>
                        {t(`clubs.${club.role}`)}
                      </span>
                    )}
                    {club.role === 'admin' && club.pending_request_count && club.pending_request_count > 0 ? (
                      <span className="clubs-pending-badge">
                        {club.pending_request_count}
                      </span>
                    ) : null}
                  </div>
                </div>
                {club.description && (
                  <p className="clubs-card-description">{club.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <ClubCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
};

export default ClubsListView;
